import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { notification, Spin, Empty, Tag, Progress } from "antd";
import moment from "moment";
import {
    HiOutlineClock,
    HiOutlineChevronLeft,
    HiOutlineChevronRight,
    HiOutlineUser,
    HiOutlineIdentification,
    HiOutlineLightningBolt
} from 'react-icons/hi';

const QueueMaster = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [queueData, setQueueData] = useState([]);
    const [stats, setStats] = useState({ total: 0, checkedIn: 0, waiting: 0, completed: 0 });
    const [filterDate, setFilterDate] = useState(moment().format('YYYY-MM-DD'));

    useEffect(() => {
        fetchQueue();
    }, [filterDate]);

    const fetchQueue = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post("doctor/getAllAppointment", {
                clinic_id: Cookies.get("Clinic_id") || Cookies.get("clinic_id"),
                doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
                search_date: filterDate
            });

            if (response.data.status === 1 && Array.isArray(response.data.data)) {
                const results = response.data.data;
                setQueueData(results);

                const completed = results.filter(r => Number(r.check_out_status ?? r.checked_out ?? r.is_checkout ?? 0) === 1).length;
                const checkedIn = results.filter(r => 
                    Number(r.check_in_status ?? r.checked_in ?? r.is_checkin ?? 0) === 1 && 
                    Number(r.check_out_status ?? r.checked_out ?? r.is_checkout ?? 0) !== 1
                ).length;
                const waiting = Math.max(0, results.length - completed - checkedIn);

                setStats({
                    total: results.length,
                    checkedIn: checkedIn,
                    waiting: waiting,
                    completed: completed
                });
            } else {
                setQueueData([]);
                setStats({ total: 0, checkedIn: 0, waiting: 0, completed: 0 });
            }
        } catch (err) {
            console.error("Failed to fetch queue", err);
            notification.error({ message: "Failed to load queue" });
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (patient) => {
        const today = moment().format('YYYY-MM-DD');
        if (filterDate !== today) {
            notification.error({
                message: "Access Denied",
                description: "Appointment details are only available for today's list.",
                duration: 4,
                style: {
                    borderRadius: '8px',
                    border: '1px solid #fee2e2',
                    backgroundColor: '#fef2f2'
                }
            });
            return;
        }
        history.push(`/dashboard/appointments/details/${patient.bookingId || patient.booking_id}`);
    };

    const getPatientTypeIcon = (type) => {
        if (type?.toLowerCase().includes('walk-in')) return <HiOutlineUser className="text-blue-500" size={13} />;
        if (type?.toLowerCase().includes('online')) return <HiOutlineLightningBolt className="text-amber-500" size={13} />;
        return <HiOutlineIdentification className="text-teal-500" size={13} />;
    };

    const getStatusTag = (app) => {
        const isCheckout = Number(app.check_out_status ?? app.checked_out ?? app.is_checkout ?? 0) === 1;
        const isCheckin = Number(app.check_in_status ?? app.checked_in ?? app.is_checkin ?? 0) === 1;
        const isArrived = String(app.arrived_status ?? app.arrived ?? app.is_arrived ?? "0") === "1";

        if (isCheckout) {
            return <Tag color="success" className="text-[11px] px-1.5 py-0 rounded m-0">Completed</Tag>;
        }

        if (isCheckin) {
            return <Tag color="processing" className="text-[11px] px-1.5 py-0 rounded m-0">In Consultation</Tag>;
        }

        if (isArrived) {
            return <Tag color="warning" className="text-[11px] px-1.5 py-0 rounded m-0">Arrived</Tag>;
        }

        return <Tag color="default" className="text-[11px] px-1.5 py-0 rounded m-0">Scheduled</Tag>;
    };

    return (
        <div className="space-y-3 animate-in fade-in duration-300 w-full">
            {/* Live Patient Queue Header Bar */}
            <div className="bg-white px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 sm:gap-0 w-full">
                <div>
                    <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">Live Patient Queue</h2>
                    <p className="text-gray-400 text-[11px] m-0 mt-0.5">Track and manage patient flow in real-time</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                    <button
                        onClick={() => setFilterDate(moment(filterDate).subtract(1, 'day').format('YYYY-MM-DD'))}
                        className="p-1 hover:bg-gray-100 rounded text-gray-400 transition-colors border-none bg-transparent cursor-pointer"
                        title="Previous Day"
                    >
                        <HiOutlineChevronLeft size={15} />
                    </button>
                    <div className="bg-[#e6f4f1] text-[#0d9488] px-3 py-1 rounded-full font-bold text-xs">
                        {moment(filterDate).format('DD MMM YYYY')}
                    </div>
                    <button
                        onClick={() => setFilterDate(moment(filterDate).add(1, 'day').format('YYYY-MM-DD'))}
                        className="p-1 hover:bg-gray-100 rounded text-gray-400 transition-colors border-none bg-transparent cursor-pointer"
                        title="Next Day"
                    >
                        <HiOutlineChevronRight size={15} />
                    </button>
                </div>
            </div>

            {/* Stats Cards - Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
                {/* TOTAL TOKENS */}
                <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">TOTAL TOKENS</p>
                        <p className="text-lg font-bold text-gray-800 leading-tight mt-1 m-0">{stats.total}</p>
                    </div>
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <Progress
                            type="circle"
                            percent={100}
                            strokeColor="#0d9488"
                            size={32}
                            format={() => <span className="text-[11px] font-bold text-[#0d9488]">{stats.total}</span>}
                        />
                    </div>
                </div>

                {/* IN CONSULTATION */}
                <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">IN CONSULTATION</p>
                        <p className="text-lg font-bold text-teal-600 leading-tight mt-1 m-0">{stats.checkedIn}</p>
                    </div>
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <Progress
                            type="circle"
                            percent={stats.total > 0 ? (stats.checkedIn / stats.total) * 100 : 0}
                            strokeColor="#0d9488"
                            size={32}
                            format={() => <span className="text-[10px] font-medium text-gray-500">{stats.total > 0 ? Math.round((stats.checkedIn / stats.total) * 100) : 0}%</span>}
                        />
                    </div>
                </div>

                {/* WAITING */}
                <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">WAITING</p>
                        <p className="text-lg font-bold text-amber-500 leading-tight mt-1 m-0">{stats.waiting}</p>
                    </div>
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <Progress
                            type="circle"
                            percent={stats.total > 0 ? (stats.waiting / stats.total) * 100 : 0}
                            strokeColor="#f59e0b"
                            size={32}
                            format={() => <span className="text-[10px] font-medium text-gray-500">{stats.total > 0 ? Math.round((stats.waiting / stats.total) * 100) : 0}%</span>}
                        />
                    </div>
                </div>

                {/* COMPLETED */}
                <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">COMPLETED</p>
                        <p className="text-lg font-bold text-emerald-600 leading-tight mt-1 m-0">{stats.completed}</p>
                    </div>
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <Progress
                            type="circle"
                            percent={stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}
                            strokeColor="#10b981"
                            size={32}
                            format={() => <span className="text-[10px] font-medium text-gray-500">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</span>}
                        />
                    </div>
                </div>
            </div>

            {/* Queue Content Container */}
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 min-h-[320px] flex flex-col justify-center w-full">
                {loading ? (
                    <div className="py-16 flex flex-col items-center justify-center gap-2">
                        <Spin size="small" />
                        <p className="text-gray-400 font-semibold text-xs m-0">Refreshing queue...</p>
                    </div>
                ) : queueData.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 w-full">
                        {queueData.map((patient, idx) => {
                            const isCheckout = Number(patient.check_out_status ?? patient.checked_out ?? patient.is_checkout ?? 0) === 1;
                            const isCheckin = Number(patient.check_in_status ?? patient.checked_in ?? patient.is_checkin ?? 0) === 1;

                            return (
                                <div
                                    key={idx}
                                    className={`relative bg-white p-3 rounded-lg border transition-all hover:shadow-xs ${
                                        isCheckout
                                            ? 'border-emerald-500 ring-1 ring-emerald-50 bg-emerald-50/10'
                                            : isCheckin
                                                ? 'border-teal-500 ring-1 ring-teal-50'
                                                : 'border-gray-200 shadow-2xs'
                                    }`}
                                >
                                    {isCheckout ? (
                                        <div className="absolute -top-2 -right-1 bg-emerald-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-2xs uppercase">
                                            Completed
                                        </div>
                                    ) : isCheckin && (
                                        <div className="absolute -top-2 -right-1 bg-teal-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-2xs uppercase">
                                            Currently In
                                        </div>
                                    )}

                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-md flex items-center justify-center font-bold text-xs ${
                                                isCheckout
                                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                    : isCheckin
                                                        ? 'bg-teal-600 text-white'
                                                        : 'bg-teal-50 text-teal-700 border border-teal-100'
                                            }`}>
                                                {patient.token_no || `#${idx + 1}`}
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-semibold text-gray-400 uppercase leading-none m-0">Token</p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    {getPatientTypeIcon(patient.appointment_type)}
                                                    <span className="text-[10px] font-bold text-gray-600 uppercase">{patient.appointment_type || 'General'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-5 h-5 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 shrink-0">
                                                <HiOutlineUser size={11} />
                                            </div>
                                            <p className="font-bold text-xs text-gray-800 line-clamp-1 m-0">{patient.patientName}</p>
                                        </div>

                                        <div className="flex items-center gap-1.5 text-gray-500">
                                            <HiOutlineClock size={13} className="text-gray-400 shrink-0" />
                                            <span className="text-[11px] font-medium">{moment(patient.appointment_time, "HH:mm:ss").format("hh:mm A")}</span>
                                        </div>

                                        <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                                            {getStatusTag(patient)}
                                            <button
                                                onClick={() => handleViewDetails(patient)}
                                                className="text-teal-600 text-[11px] font-bold hover:underline uppercase tracking-wide border-none bg-transparent cursor-pointer"
                                            >
                                                Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="py-16 flex flex-col items-center justify-center">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<span className="text-xs font-semibold text-gray-400">Queue is empty for today</span>}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default QueueMaster;